import { Fragment, useState, useCallback, useEffect } from 'react';
import { Card, CardHeader, Button, Badge, Input, Select, LoadingSkeleton, Pagination, Modal } from '@nexora/design-system';
import {
  useModuleCtx,
  useOkrDashboard,
  useOkrs,
  useCreateOkr,
  useUpdateOkr,
  useDeleteOkr,
  useOkrTransition,
  useAddKeyResult,
  useUpdateKeyResultProgress,
  type Okr,
  type OkrInput,
  type KeyResult,
  type OkrStatus,
  OKR_STATUS_LABEL,
  okrStatusBadgeVariant,
  KEY_RESULT_STATUS_LABEL,
} from './useOkrs';
import { formatDateTime, formatNumber } from '../../shared/utils/format';

/* ═══════════════════════════════════════════════════════════════════════════
   OKRs — Gestão de OKRs e Resultados-Chave
   (protótipo `erpone-gest-o-de-okrs-e-resultados-chave`)
   ───────────────────────────────────────────────────────────────────────────
   Contratos consumidos (backend real, DTOs em okrs.dto.ts):
   - GET  /okrs/dashboard → OkrDashboardDto (okrs, atividadesRecentes, resumo)
   - GET  /okrs           → PaginatedOkrResponseDto
   - POST /okrs, PATCH/DELETE /okrs/:id
   - POST /okrs/:id/activate | complete | archive
   - POST /okrs/:id/key-results
   - PATCH /okrs/:id/key-results/:krId/progress

   Progresso é 0-100 (número) — exibido como "68%". resumo.progressoMedio
   também 0-100.
   ═══════════════════════════════════════════════════════════════════════════ */

interface ToastState {
  message: string;
  variant: 'success' | 'error';
}

function scorePercent(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${formatNumber(value, { maximumFractionDigits: 1 })}%`;
}

/* ── Modal: Criar / Editar OKR ── */

function OkrFormModal({
  isOpen,
  onClose,
  okr,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  okr?: Okr;
  onSubmit: (data: OkrInput) => Promise<void>;
}): JSX.Element {
  const [periodo, setPeriodo] = useState('');
  const [titulo, setTitulo] = useState('');
  const [objetivo, setObjetivo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (okr) {
      setPeriodo(okr.periodo);
      setTitulo(okr.titulo);
      setObjetivo(okr.objetivo);
      setDescricao(okr.descricao ?? '');
    } else {
      setPeriodo('');
      setTitulo('');
      setObjetivo('');
      setDescricao('');
    }
    setError(null);
  }, [okr, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!periodo.trim()) { setError('Período é obrigatório (ex.: Q3 2026).'); return; }
    if (!titulo.trim()) { setError('Título é obrigatório.'); return; }
    if (!objetivo.trim()) { setError('Objetivo é obrigatório.'); return; }
    try {
      setSubmitting(true);
      await onSubmit({
        periodo: periodo.trim(),
        titulo: titulo.trim(),
        objetivo: objetivo.trim(),
        descricao: descricao.trim() || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar OKR.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={okr ? 'Editar OKR' : 'Novo OKR'} size="md">
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Input label="Período" value={periodo} onChange={(e) => setPeriodo(e.target.value)} required placeholder="Ex.: Q3 2026" />
          <Input label="Título" value={titulo} onChange={(e) => setTitulo(e.target.value)} required placeholder="Ex.: Q3 Company OKRs" />
          <Input label="Objetivo" value={objetivo} onChange={(e) => setObjetivo(e.target.value)} required placeholder="Ex.: Expandir participação de mercado" />
          <Input label="Descrição (opcional)" value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Detalhes do ciclo" />
          {error && (
            <div style={{ padding: '0.625rem 0.75rem', borderRadius: '8px', background: '#ffdad6', border: '1px solid #ffb4ab', color: '#ba1a1a', fontSize: '0.8125rem' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Salvando...' : okr ? 'Salvar alterações' : 'Criar OKR'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

/* ── Modal: Adicionar Key Result ── */

function KeyResultModal({
  isOpen,
  onClose,
  okr,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  okr: Okr;
  onSubmit: (data: { titulo: string; meta: number; inicial?: number; atual?: number }) => Promise<void>;
}): JSX.Element {
  const [titulo, setTitulo] = useState('');
  const [inicial, setInicial] = useState('0');
  const [atual, setAtual] = useState('0');
  const [meta, setMeta] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTitulo('');
    setInicial('0');
    setAtual('0');
    setMeta('');
    setError(null);
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const metaNum = Number(meta);
    if (!titulo.trim()) { setError('Título do key result é obrigatório.'); return; }
    if (!Number.isFinite(metaNum) || metaNum <= 0) { setError('Meta deve ser um número maior que zero.'); return; }
    try {
      setSubmitting(true);
      await onSubmit({
        titulo: titulo.trim(),
        meta: metaNum,
        inicial: Number(inicial) || 0,
        atual: Number(atual) || 0,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao adicionar key result.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Adicionar Key Result — ${okr.titulo}`} size="md">
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Input label="Título" value={titulo} onChange={(e) => setTitulo(e.target.value)} required placeholder="Ex.: Fechar 5 novos contratos Enterprise" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
            <Input label="Inicial" type="number" value={inicial} onChange={(e) => setInicial(e.target.value)} />
            <Input label="Atual" type="number" value={atual} onChange={(e) => setAtual(e.target.value)} />
            <Input label="Meta" type="number" value={meta} onChange={(e) => setMeta(e.target.value)} required placeholder="Ex.: 5" />
          </div>
          {error && (
            <div style={{ padding: '0.625rem 0.75rem', borderRadius: '8px', background: '#ffdad6', border: '1px solid #ffb4ab', color: '#ba1a1a', fontSize: '0.8125rem' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Salvando...' : 'Adicionar key result'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

/* ── Modal: Atualizar progresso do Key Result ── */

function ProgressModal({
  isOpen,
  onClose,
  okr,
  keyResult,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  okr: Okr;
  keyResult: KeyResult | null;
  onSubmit: (data: { atual?: number; percentual?: number }) => Promise<void>;
}): JSX.Element {
  const [atual, setAtual] = useState('');
  const [percentual, setPercentual] = useState('');
  const [mode, setMode] = useState<'valor' | 'percentual'>('valor');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (keyResult) {
      setAtual(String(keyResult.atual));
      setPercentual(String(keyResult.progresso));
    }
    setError(null);
  }, [keyResult, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!mode) { setError('Escolha o modo de atualização.'); return; }
    if (mode === 'valor') {
      const num = Number(atual);
      if (!Number.isFinite(num)) { setError('Valor atual inválido.'); return; }
      try {
        setSubmitting(true);
        await onSubmit({ atual: num });
        onClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao atualizar progresso.');
      } finally {
        setSubmitting(false);
      }
    } else {
      const num = Number(percentual);
      if (!Number.isFinite(num) || num < 0 || num > 100) { setError('Percentual deve estar entre 0 e 100.'); return; }
      try {
        setSubmitting(true);
        await onSubmit({ percentual: num });
        onClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao atualizar progresso.');
      } finally {
        setSubmitting(false);
      }
    }
  };

  if (!keyResult) return <></>;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Atualizar progresso — ${keyResult.titulo}`} size="md">
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ fontSize: '0.875rem', color: '#43474e' }}>
            <span style={{ fontWeight: 600 }}>{okr.titulo}</span> · progresso atual:{' '}
            <strong>{scorePercent(keyResult.progresso)}</strong> ({keyResult.atual} / {keyResult.meta})
          </div>
          <Select label="Modo de atualização" value={mode} onChange={(e) => setMode(e.target.value as 'valor' | 'percentual')}>
            <option value="valor">Valor atual (atual / meta)</option>
            <option value="percentual">Percentual direto (%)</option>
          </Select>
          {mode === 'valor' ? (
            <Input label="Valor atual" type="number" value={atual} onChange={(e) => setAtual(e.target.value)} required />
          ) : (
            <Input label="Percentual (%)" type="number" value={percentual} onChange={(e) => setPercentual(e.target.value)} required min={0} max={100} />
          )}
          {error && (
            <div style={{ padding: '0.625rem 0.75rem', borderRadius: '8px', background: '#ffdad6', border: '1px solid #ffb4ab', color: '#ba1a1a', fontSize: '0.8125rem' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Salvando...' : 'Salvar progresso'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

/* ── Linha de Key Result (expansível com histórico) ── */

function KeyResultRow({
  okr,
  kr,
  onUpdateProgress,
}: {
  okr: Okr;
  kr: KeyResult;
  onUpdateProgress: (kr: KeyResult) => void;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const pct = Math.max(0, Math.min(100, kr.progresso));

  return (
    <>
      <tr
        style={{ cursor: 'pointer', background: expanded ? '#f6f8fa' : undefined }}
        onClick={() => setExpanded(!expanded)}
      >
        <td style={{ padding: '0.625rem 1rem', borderBottom: '1px solid #eceef0', paddingLeft: '2.5rem' }}>
          <span style={{ fontWeight: 500 }}>{kr.titulo}</span>
          {kr.descricao && (
            <span style={{ display: 'block', fontSize: '0.75rem', color: '#74777f' }}>{kr.descricao}</span>
          )}
        </td>
        <td style={{ padding: '0.625rem 1rem', borderBottom: '1px solid #eceef0' }}>
          <Badge variant={kr.status === 'COMPLETED' ? 'success' : kr.status === 'NOT_STARTED' ? 'neutral' : 'info'} size="sm">
            {KEY_RESULT_STATUS_LABEL[kr.status]}
          </Badge>
        </td>
        <td style={{ padding: '0.625rem 1rem', borderBottom: '1px solid #eceef0', textAlign: 'right', whiteSpace: 'nowrap' }}>
          {kr.atual} / {kr.meta}
        </td>
        <td style={{ padding: '0.625rem 1rem', borderBottom: '1px solid #eceef0', minWidth: '180px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ flex: 1, height: '8px', background: '#e2e5e8', borderRadius: '999px', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  width: `${pct}%`,
                  background: kr.progresso >= 100 ? '#2e7d32' : kr.progresso >= 60 ? '#002045' : '#ed6c02',
                  borderRadius: '999px',
                }}
              />
            </div>
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#191c1e', width: '44px', textAlign: 'right' }}>
              {scorePercent(kr.progresso)}
            </span>
          </div>
        </td>
        <td style={{ padding: '0.625rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap', textAlign: 'right' }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onUpdateProgress(kr); }}
          >
            Atualizar
          </Button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5} style={{ padding: '0.75rem 1.5rem 1rem 2.5rem', background: '#f6f8fa' }}>
            <div style={{ fontSize: '0.875rem', color: '#43474e', maxHeight: '220px', overflowY: 'auto' }}>
              <strong style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8125rem' }}>Activity History</strong>
              {kr && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {okr.activities.length === 0 && (
                    <span style={{ color: '#74777f', fontSize: '0.8125rem' }}>Sem atividades registradas para este ciclo.</span>
                  )}
                  {okr.activities.slice(0, 6).map((a, i) => (
                    <div key={`${a.at}-${i}`} style={{ display: 'flex', gap: '0.625rem', fontSize: '0.8125rem' }}>
                      <span style={{ color: '#74777f', whiteSpace: 'nowrap' }}>{formatDateTime(a.at)}</span>
                      <span>
                        <strong>{a.actorName}</strong> — {a.action} ({a.detail})
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ── Card central da página ── */

export function OkrsPage(): JSX.Element {
  const ctx = useModuleCtx();
  const { data: dashboard, isLoading: dashLoading, error: dashError } = useOkrDashboard(ctx);
  const { data: list, isLoading: listLoading, error: listError } = useOkrs(ctx, { limit: 20 });
  const createMutation = useCreateOkr(ctx);
  const updateMutation = useUpdateOkr();
  const deleteMutation = useDeleteOkr(ctx);
  const transitionMutation = useOkrTransition(ctx);
  const addKrMutation = useAddKeyResult(ctx);
  const progressMutation = useUpdateKeyResultProgress(ctx);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingOkr, setEditingOkr] = useState<Okr | undefined>(undefined);
  const [krModalOkr, setKrModalOkr] = useState<Okr | null>(null);
  const [progressTarget, setProgressTarget] = useState<{ okr: Okr; kr: KeyResult } | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [toast, setToast] = useState<ToastState | null>(null);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const handleCreate = useCallback(
    async (input: OkrInput) => {
      try {
        await createMutation.mutateAsync(input);
        showToast('OKR criado com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao criar OKR.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [createMutation, showToast],
  );

  const handleUpdate = useCallback(
    async (input: OkrInput) => {
      if (!editingOkr) return;
      try {
        await updateMutation.mutateAsync({ id: editingOkr.id, ...input });
        showToast('OKR atualizado com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao atualizar OKR.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [editingOkr, updateMutation, showToast],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!window.confirm('Tem certeza que deseja excluir este OKR? (apenas rascunhos)')) return;
      try {
        await deleteMutation.mutateAsync(id);
        showToast('OKR excluído com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao excluir OKR.';
        showToast(msg, 'error');
      }
    },
    [deleteMutation, showToast],
  );

  const handleTransition = useCallback(
    async (okr: Okr, transition: 'activate' | 'complete' | 'archive') => {
      const labels = { activate: 'ativado', complete: 'concluído', archive: 'arquivado' } as const;
      if (transition === 'archive' && !window.confirm('Arquivar este OKR?')) return;
      try {
        await transitionMutation.mutateAsync({ id: okr.id, transition });
        showToast(`OKR ${labels[transition]} com sucesso.`, 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao alterar status do OKR.';
        showToast(msg, 'error');
      }
    },
    [transitionMutation, showToast],
  );

  const handleAddKeyResult = useCallback(
    async (data: { titulo: string; meta: number; inicial?: number; atual?: number }) => {
      if (!krModalOkr) return;
      try {
        await addKrMutation.mutateAsync({ okrId: krModalOkr.id, ...data });
        showToast('Key result adicionado com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao adicionar key result.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [krModalOkr, addKrMutation, showToast],
  );

  const handleProgress = useCallback(
    async (data: { atual?: number; percentual?: number }) => {
      if (!progressTarget) return;
      try {
        await progressMutation.mutateAsync({
          okrId: progressTarget.okr.id,
          krId: progressTarget.kr.id,
          ...data,
        });
        showToast('Progresso atualizado com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao atualizar progresso.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [progressTarget, progressMutation, showToast],
  );

  const resumo = dashboard?.resumo;
  const okrs = list?.data ?? [];
  const filtered = statusFilter === 'ALL' ? okrs : okrs.filter((o) => o.status === (statusFilter as OkrStatus));
  const isLoading = dashLoading || listLoading;
  const error = dashError ?? listError;

  const statusOptions: { value: string; label: string }[] = [
    { value: 'ALL', label: 'Todos os status' },
    ...(Object.keys(OKR_STATUS_LABEL) as OkrStatus[]).map((s) => ({ value: s, label: OKR_STATUS_LABEL[s] })),
  ];

  return (
    <div className="module-page">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700, color: '#191c1e' }}>OKRs</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#43474e', fontSize: '0.875rem' }}>
            Gestão de OKRs e resultados-chave — {dashboard?.resumo.total ?? 0} ciclos.
          </p>
        </div>
        <Button variant="primary" onClick={() => { setEditingOkr(undefined); setModalOpen(true); }}>+ Novo OKR</Button>
      </div>

      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Progresso médio', value: scorePercent(resumo?.progressoMedio), color: '#002045' },
          { label: 'Ativos', value: String(resumo?.ativos ?? 0), color: '#191c1e' },
          { label: 'Concluídos', value: String(resumo?.concluidos ?? 0), color: '#2e7d32' },
          { label: 'Arquivados', value: String(resumo?.arquivados ?? 0), color: '#74777f' },
        ].map((s) => (
          <div key={s.label} style={{ background: '#fff', border: '1px solid #e0e3e5', borderRadius: '10px', padding: '1rem' }}>
            <p style={{ margin: 0, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#74777f' }}>{s.label}</p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '1.5rem', fontWeight: 700, color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Dashboard summary: metas departamentais */}
      {dashboard && (
        <Card variant="outlined" className="mb-md">
                <CardHeader>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Resumo do ciclo</h3>
                </CardHeader>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', padding: '0 1.25rem 1.25rem', fontSize: '0.875rem', color: '#43474e' }}>
            <span><strong>{resumo?.metasDepartamentais ?? 0}</strong> metas departamentais</span>
            <span>Peso total das metas: <strong>{scorePercent(resumo?.pesoTotalMetas ?? 0)}</strong></span>
          </div>
        </Card>
      )}

      {/* Filter + list */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#191c1e' }}>
          OKRs
          <span style={{ fontSize: '0.875rem', fontWeight: 400, color: '#74777f', marginLeft: '0.5rem' }}>
            ({filtered.length} {filtered.length === 1 ? 'ciclo' : 'ciclos'})
          </span>
        </h2>
        <div style={{ width: '220px' }}>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {statusOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </div>
      </div>

      <Card variant="outlined" padding="0">
        <CardHeader className="px-md pt-md">
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Ciclos de OKRs</h3>
        </CardHeader>
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
            <LoadingSkeleton />
            <span style={{ fontSize: '0.875rem' }}>Carregando OKRs...</span>
          </div>
        )}
        {error && !isLoading && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar OKRs</p>
            <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>
              {error instanceof Error ? error.message : 'Erro desconhecido'}
            </p>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()} style={{ marginTop: '1rem' }}>
              Tentar novamente
            </Button>
          </div>
        )}
        {!isLoading && !error && filtered.length === 0 && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f' }}>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>Nenhum OKR encontrado para este filtro.</p>
            <Button variant="outline" size="sm" onClick={() => { setEditingOkr(undefined); setModalOpen(true); }}>Criar primeiro OKR</Button>
          </div>
        )}
        {!isLoading && !error && filtered.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Período', 'Título / Objetivo', 'Status', 'Progresso', 'Ações'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '0.75rem 1rem',
                        background: '#f2f4f6',
                        color: '#43474e',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid #e0e3e5',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((okr) => (
                  <OKRGroup
                    key={okr.id}
                    okr={okr}
                    onEdit={() => { setEditingOkr(okr); setModalOpen(true); }}
                    onDelete={() => handleDelete(okr.id)}
                    onTransition={(t) => handleTransition(okr, t)}
                    onAddKr={() => setKrModalOkr(okr)}
                    onUpdateProgress={(kr) => setProgressTarget({ okr, kr })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!isLoading && !error && (list?.totalPages ?? 1) > 1 && (
          <div style={{ padding: '1rem', borderTop: '1px solid #eceef0' }}>
            <Pagination page={list?.page ?? 1} totalPages={list?.totalPages ?? 1} onChange={() => {}} />
          </div>
        )}
      </Card>

      {/* Modals */}
      <OkrFormModal isOpen={modalOpen} onClose={() => setModalOpen(false)} okr={editingOkr} onSubmit={editingOkr ? handleUpdate : handleCreate} />
      {krModalOkr && (
        <KeyResultModal
          isOpen={!!krModalOkr}
          onClose={() => setKrModalOkr(null)}
          okr={krModalOkr}
          onSubmit={handleAddKeyResult}
        />
      )}
      {progressTarget && (
        <ProgressModal
          isOpen={!!progressTarget}
          onClose={() => setProgressTarget(null)}
          okr={progressTarget.okr}
          keyResult={progressTarget.kr}
          onSubmit={handleProgress}
        />
      )}

      {toast && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            right: '1.5rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            background: toast.variant === 'success' ? '#d3f5d6' : '#ffdad6',
            border: `1px solid ${toast.variant === 'success' ? '#b6ebbb' : '#ffb4ab'}`,
            color: toast.variant === 'success' ? '#2e7d32' : '#ba1a1a',
            fontSize: '0.875rem',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 10000,
            maxWidth: '400px',
          }}
        >
          {toast.message}
          <button
            onClick={() => setToast(null)}
            aria-label="Fechar"
            style={{ marginLeft: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1rem', padding: 0, lineHeight: 1 }}
          >
            &times;
          </button>
        </div>
      )}

      <style>{`
        .module-page { padding: 1.5rem; }
        .mb-md { margin-bottom: 1rem; }
      `}</style>
    </div>
  );
}

/* ── Grupo OKR: cabeçalho com key results ── */

function OKRGroup({
  okr,
  onEdit,
  onDelete,
  onTransition,
  onAddKr,
  onUpdateProgress,
}: {
  okr: Okr;
  onEdit: () => void;
  onDelete: () => void;
  onTransition: (t: 'activate' | 'complete' | 'archive') => void;
  onAddKr: () => void;
  onUpdateProgress: (kr: KeyResult) => void;
}): JSX.Element {
  const [expanded, setExpanded] = useState(true);
  const pct = Math.max(0, Math.min(100, okr.progresso));

  return (
    <>
      <tr style={{ background: '#f2f4f6', cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e0e3e5', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {expanded ? '▼' : '▶'} {okr.periodo}
        </td>
        <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e0e3e5' }}>
          <span style={{ fontWeight: 600 }}>{okr.titulo}</span>
          <span style={{ display: 'block', fontSize: '0.8125rem', color: '#43474e' }}>{okr.objetivo}</span>
        </td>
        <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e0e3e5' }}>
          <Badge variant={okrStatusBadgeVariant(okr.status)} size="sm">{OKR_STATUS_LABEL[okr.status]}</Badge>
        </td>
        <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e0e3e5' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ flex: 1, height: '8px', background: '#e2e5e8', borderRadius: '999px', overflow: 'hidden', minWidth: '120px' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: '#002045', borderRadius: '999px' }} />
            </div>
            <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{scorePercent(okr.progresso)}</span>
          </div>
          <span style={{ fontSize: '0.75rem', color: '#74777f' }}>
            {okr.keyResults.filter((k) => k.status === 'COMPLETED').length}/{okr.keyResults.length} key results concluídos
          </span>
        </td>
        <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e0e3e5', whiteSpace: 'nowrap', textAlign: 'right' }}>
          <Button variant="ghost" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); onAddKr(); }}>+ Key Result</Button>
          {okr.status === 'DRAFT' && (
            <Button variant="ghost" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); onTransition('activate'); }}>Ativar</Button>
          )}
          {okr.status === 'ACTIVE' && (
            <Button variant="ghost" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); onTransition('complete'); }}>Concluir</Button>
          )}
          {(okr.status === 'DRAFT' || okr.status === 'COMPLETED') && (
            <Button variant="ghost" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); onTransition('archive'); }}>Arquivar</Button>
          )}
          <Button variant="ghost" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); onEdit(); }}>Editar</Button>
          {okr.status === 'DRAFT' && (
            <Button variant="ghost" size="sm" style={{ color: '#ba1a1a' }} onClick={(e: React.MouseEvent) => { e.stopPropagation(); onDelete(); }}>Excluir</Button>
          )}
        </td>
      </tr>
      {expanded && okr.keyResults.length > 0 && (
        <Fragment key="kr-list">
          {okr.keyResults.map((kr) => (
            <KeyResultRow key={kr.id} okr={okr} kr={kr} onUpdateProgress={onUpdateProgress} />
          ))}
        </Fragment>
      )}
      {expanded && okr.keyResults.length === 0 && (
        <tr>
          <td colSpan={5} style={{ padding: '0.75rem 1rem 1rem 2.5rem', background: '#fafbfc', color: '#74777f', fontSize: '0.8125rem' }}>
            Nenhum key result neste ciclo. Clique em “+ Key Result” para adicionar.
          </td>
        </tr>
      )}
    </>
  );
}